import { z, ZodArray, ZodObject, ZodRawShape } from "zod";
import { gql } from "@apollo/client";
import * as gqlBuilder from "gql-query-builder";
import { GraphQLClient } from "../../../../../shared/gql-client";
import {
  CustomFilters,
  GraphqlFilterRoot,
  GraphqlRelationShip,
  GraphqlScalarString,
  GraphqlSortField,
} from "../../domain/models/graphqlModel";
import { SortingState } from "@tanstack/react-table";
import { ZodTypeAny } from "zod/lib/types";

interface ReadAllFields {
  data: (string | { [key: string]: string | unknown[] })[]; // Exact type for data
  paginatorInfo?: string[]; // Optional paginatorInfo field
}
interface ReadAllVariablesModel {
  page?: number;
  first?: number;
  [key: string]: unknown;
}

export default class CrudService {
  private client: GraphQLClient | undefined;
  private schema: ZodObject<ZodRawShape>;

  constructor() {
    this.client = undefined;
    this.schema = z.object({});
  }

  init(client: GraphQLClient, schema: ZodObject<ZodRawShape>) {
    this.client = client;
    this.schema = schema;
  }

  private generateDataString(
    data: z.infer<typeof this.schema>,
    mutationExclude?: string[],
    schema?: ZodObject<ZodRawShape>,
    mutateData?: boolean,
  ): string {
    const currentSchema = schema !== undefined ? schema : this.schema;

    return Object.keys(currentSchema.shape)
      .filter(
        (key) =>
          data[key] != null ||
          (currentSchema.shape[key] instanceof z.ZodNullable &&
            currentSchema.shape[key].description == "can_be_null"),
      )
      .filter((key) => !mutationExclude?.includes(key))
      .map((key) => {
        if (key.includes("qrFieldOptions")) return;
        if ((key == "upsert" || key == "delete") && mutateData) {
          if (Array.isArray(data[key])) {
            const shape = currentSchema.shape[key] as ZodArray<ZodTypeAny>;
            const element = shape.element as ZodObject<ZodRawShape>;

            let returnString = `${key}: [`;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            data[key].forEach((item: object, i: number) => {
              if (typeof item === "string") {
                returnString += item;
                if (i < data[key].length - 1) {
                  returnString += `,`;
                }
              } else {
                returnString += `{${this.generateDataString(
                  item,
                  undefined,
                  element,
                  mutateData,
                )}}`;
              }
            });

            returnString += `]`;
            return returnString;
          }

          return `${key}: {${this.generateDataString(
            data[key],
            undefined,
            currentSchema.shape[key] as ZodObject<ZodRawShape>,
            mutateData,
          )}}`;
        } else if (key == "upsert" || key == "delete") {
          return;
        }
        if (data[key] == null) {
          return `${key}: null`;
        } else if (typeof data[key] === "string") {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          if (data[key].includes("[") && data[key].includes("]")) {
            return `${key}: ${data[key]}`;
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          return `${key}: "${data[key]}"`;
        } else if (data[key] instanceof Date) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          return `${key}: "${data[key].toISOString().split("T")[0]}"`;
        } else if (Array.isArray(data[key])) {
          const array = data[key] as string[];

          return `${key}: { sync: [${array
            .map((item, index) => {
              return `{ id: "${item}", weight: ${index} }`;
            })
            .join(", ")}] }`;
        } else if (typeof data[key] === "object") {
          return `${key}: {${this.generateDataString(
            data[key],
            undefined,
            currentSchema.shape[key] as ZodObject<ZodRawShape>,
            mutateData,
          )}}`;
        } else {
          return `${key}: ${data[key]}`;
        }
      })
      .join(", ");
  }

  private generateSubFieldsString(
    queryExclude?: string[],
    schema?: ZodObject<ZodRawShape>,
  ): string {
    const currentSchema = schema !== undefined ? schema : this.schema;

    return Object.keys(currentSchema.shape)
      .filter((key) => !queryExclude?.includes(key))
      .map((key) => {
        if (key == "upsert" || key == "delete") return;

        if (key.endsWith("_id") && !key.includes("source")) {
          return `${key.replace("_id", "")} {
                    id
                }`;
        }

        if (
          key === "relations" &&
          currentSchema.shape[key] instanceof z.ZodArray
        ) {
          return `${key} {
            id
          }`;
        }

        if (currentSchema.shape[key] instanceof z.ZodObject) {
          const objectString = this.generateSubFieldsString(
            queryExclude,
            currentSchema.shape[key] as ZodObject<ZodRawShape>,
          );

          return `${key} {
                    ${objectString}
                }`;
        }
        return key;
      })
      .join("\n");
  }

  // new functions

  private generateDataFields(
    queryExclude?: string[],
    schema?: ZodObject<ZodRawShape>,
  ): (
    | {
        [p: string]: ({ [p: string]: unknown } | string)[];
      }
    | string
  )[] {
    const currentSchema = schema !== undefined ? schema : this.schema;

    return Object.keys(currentSchema.shape)
      .filter((key) => !queryExclude?.includes(key))
      .map((key) => {
        if (key == "upsert" || key == "delete") return "";

        if (currentSchema.shape[key] instanceof z.ZodObject) {
          return {
            [key]: this.generateDataFields(
              [],
              currentSchema.shape[key] as ZodObject<ZodRawShape>,
            ),
          };
        }

        if (
          currentSchema.shape[key] instanceof z.ZodArray &&
          !key.includes("availableRoles") &&
          !key.includes("availableTransitions")
        ) {
          return `${key} {
                    id
                }`;
        }

        if (key.endsWith("_id")) {
          if (key.startsWith("subject") || key.startsWith("causer")) return key;
          return `${key.replace("_id", "")} {
                id
            }`;
        }
        return key;
      });
  }

  private setFilterValues = (
    key: string,
    filters?: GraphqlFilterRoot | GraphqlRelationShip,
  ): { field: { [key: string]: unknown } } => {
    const value = filters?.fields[key];
    const result =
      typeof value === "object" && "fields" in value
        ? this.fixFilters(value as GraphqlRelationShip)
        : value;

    return { field: { [key]: result } };
  };

  private fixFilters = (
    filters?: GraphqlFilterRoot | GraphqlRelationShip,
    relationship?: string,
  ) => {
    if (!filters || !filters.fields) return undefined;

    const buildFilters = (filters: GraphqlFilterRoot | GraphqlRelationShip) => {
      const andFilters: { field: { [key: string]: unknown } }[] = [];
      const orFilters: { field: { [key: string]: unknown } }[] = [];

      Object.entries(filters.fields).forEach(([key, value]) => {
        const filter = this.setFilterValues(key, filters);
        const clonedFilter = structuredClone(filter);
        const condition = (value as GraphqlScalarString)?.condition || "and";

        if (!("fields" in value) || !value.fields) {
          delete (clonedFilter.field[key] as GraphqlScalarString).condition;
          (condition === "or" ? orFilters : andFilters).push(clonedFilter);
        } else {
          const innerFilter = this.fixFilters(
            value as GraphqlRelationShip,
            key,
          );

          if (innerFilter?.where) {
            clonedFilter.field[key] = innerFilter.where;
            (condition === "or" ? orFilters : andFilters).push(clonedFilter);
          }
        }
      });

      return { allOf: andFilters, anyOf: orFilters };
    };

    const { allOf, anyOf } = buildFilters(filters);
    const type = (filters as GraphqlFilterRoot)?.searchBy;

    // Special case: if searchBy is 'SearchByRootPosition' and only filtering by label, return { label: value } at top level
    if (
      type === "SearchByRootPosition" &&
      allOf.length === 1 &&
      Object.keys(allOf[0].field).length === 1 &&
      Object.keys(allOf[0].field)[0] === "label"
    ) {
      const labelFilter = allOf[0].field["label"];
      if (
        typeof labelFilter === "object" &&
        labelFilter !== null &&
        Object.keys(labelFilter).length === 1
      ) {
        return { label: Object.values(labelFilter)[0] };
      }
      // Otherwise, return as is
      return { label: labelFilter };
    }

    // If nested relationship, return `where` with nested filters.
    if (relationship) {
      return {
        where: {
          where:
            allOf.length === 1 && anyOf.length === 0
              ? { field: allOf[0].field }
              : anyOf.length === 1 && allOf.length === 0
                ? { field: anyOf[0].field }
                : {
                    ...(allOf.length > 0 ? { allOf } : {}),
                    ...(anyOf.length > 0 ? { anyOf } : {}),
                  },
        },
      };
    }

    // Top-level `allOf` structure remains as expected.
    const combinedFilters = [
      ...(allOf.length ? [{ allOf }] : []),
      ...(anyOf.length ? [{ anyOf }] : []),
    ];

    return type
      ? { where: { type, value: { allOf: combinedFilters } } }
      : {
          where: {
            field:
              combinedFilters.length === 1
                ? combinedFilters[0]
                : { allOf: combinedFilters },
          },
        };
  };

  private fixSort = (
    sorting: SortingState | undefined,
    entity: string,
  ): object | undefined => {
    if (!sorting) return undefined;

    const sortings: { nullsLast: GraphqlSortField }[] = [];

    sorting.forEach((sort) => {
      const parts = sort.id.split(".").reverse();
      let result = {
        [parts[0]]: sort.desc ? "Desc" : "Asc",
      } as GraphqlSortField;

      for (let i = 1; i < parts.length; i++) {
        result = { [parts[i]]: result };
      }

      sortings.push({ nullsLast: result });
    });

    return {
      sort: {
        type: "[SortByRoot" + entity + "!]",
        value: sortings,
      },
    };
  };

  export(
    queryFunction: string,
    queryData: (string | { [key: string]: string | unknown[] })[],
    filters?: GraphqlFilterRoot,
  ) {
    return new Promise((resolve, reject) => {
      const variables: ReadAllVariablesModel = {
        ...this.fixFilters(filters),
      };

      const fields: ReadAllFields = {
        data: queryData,
      };

      const { query, variables: gqlVariables } = gqlBuilder.query({
        operation: queryFunction,
        variables,
        fields: [fields, "url", "filename", "mimeType"],
      });

      this.client
        ?.query(queryFunction, gql(query), gqlVariables)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  readAll(
    entity: string,
    queryFunction: string,
    page: number,
    limit: number,
    filters?: GraphqlFilterRoot | CustomFilters,
    sorting?: SortingState,
    queryExclude?: string[],
    customVariables?: { [key: string]: unknown } | undefined,
  ) {
    return new Promise((resolve, reject) => {
      let varFilters = undefined;
      let isCustom = false;

      if (filters && "__typename" in filters) {
        isCustom = true;
        varFilters = { ...filters };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        delete varFilters.__typename;
      } else {
        varFilters = this.fixFilters(filters);
      }

      const variables: ReadAllVariablesModel = {
        ...this.fixSort(sorting, entity),
        ...varFilters,
        ...customVariables,
      };

      if (!isCustom) {
        const label = (filters as GraphqlFilterRoot)?.label;
        if (label) {
          variables.label = label;
        }
      }

      const fields: ReadAllFields = {
        data: this.generateDataFields(queryExclude),
      };

      if (limit !== 0) variables.first = limit;
      if (page !== 0) {
        variables.page = page;
        fields.paginatorInfo = ["total"];
      }

      const { query, variables: gqlVariables } = gqlBuilder.query({
        operation: queryFunction,
        variables,
        fields: [fields],
      });

      this.client
        ?.query(queryFunction, gql(query), gqlVariables)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  get(
    id: number,
    queryFunction: string,
    queryExclude?: string[],
    filters?: GraphqlFilterRoot,
  ): Promise<object> {
    return new Promise((resolve, reject) => {
      const { query, variables } = gqlBuilder.query({
        operation: queryFunction,
        variables: {
          id: {
            name: "id",
            type: "ID",
            value: id,
          },
          ...this.fixFilters(filters),
        },
        fields: [
          {
            data: this.generateDataFields(queryExclude),
          },
        ],
      });

      this.client
        ?.query(queryFunction, gql(query), variables)
        .then((response) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          const data = (response as z.infer<typeof this.schema>).data.map(
            (item: { [x: string]: undefined }) => {
              Object.keys(item).forEach((key) => {
                if (item[key] === null) {
                  item[key] = undefined;
                }
              });
              return item;
            },
          );
          resolve({ data: data });
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  mutate(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    data: z.infer<typeof this.schema>,
    queryFunction: string,
    queryExclude?: string[],
    mutationExclude?: string[],
  ): Promise<object> {
    const mutationFunction =
      queryFunction.charAt(0).toUpperCase() + queryFunction.slice(1);

    return new Promise((resolve, reject) => {
      const query = gql(`
                mutation ${mutationFunction} {
                    ${queryFunction}(${this.generateDataString(
                      data,
                      mutationExclude,
                      undefined,
                      true,
                    )}) {
                        ${this.generateSubFieldsString(queryExclude)}
                    }
                }
            `);

      this.client
        ?.mutate(queryFunction, query)
        .then((response) => {
          resolve(response as object);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }
}
